import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Button,
  Col,
  Row,
  Table,
  Badge,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import ArrowUpIcon from 'mdi-react/ArrowUpIcon';
import ArrowDownIcon from 'mdi-react/ArrowDownIcon';
import EditOutlineIcon from 'mdi-react/EditOutlineIcon';
import ContentCopyIcon from 'mdi-react/ContentCopyIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import moment from 'moment';
import Loading from '../../shared/components/custom/Loading';
import useModal from '../../shared/hooks/useModal';
import Api from '../../util/api';
import useConfig from '../../util/useConfig';
import DeleteModal from '../../shared/components/custom/DeleteModal';
import Pagination from '../../shared/components/pagination/Pagination';
import t, { partial } from '../../util/translation/translation';
import { formatDateTime, formatDate } from '../../util/date-and-time';
import FormCard from '../../shared/components/custom/FormCard';
import SquareIconButton from '../../shared/components/custom/SquareIconButton';
import MatchQuestionsModal from './components/MatchQuestionsModal';

const QuestionaireListPage = ({ setLoading }) => {
  const history = useHistory();
  const [questionaires, setQuestionaires] = useState([]);
  const config = useConfig();
  const [showMatchQuizzes, setShowMatchQuizzes] = useState(false);
  const [showMatchPolls, setShowMatchPolls] = useState(false);
  const [handleModal, visible, deleteID] = useModal();
  const [pagination, setPagination] = useState({
    total: 0,
    currentPage: 1,
    perPage: 10,
  });
  const [order, setOrder] = useState({
    orderBy: 'createdAt',
    desc: true,
  });
  const UNLIMITED_DATE = '9999-12-31T00:00:00.000Z';
  const p = partial('questionaire');
  const s = partial('shared');

  const fetchQuestionaires = async (
    currentPage = 1,
    pageSize = 10,
    orderObject = {
      orderBy: 'createdAt',
      desc: true,
    }
  ) => {
    setLoading(true);
    try {
      const reqQuestionaires = await Api.questionaire.getQuestionaires(
        { page: currentPage, take: pageSize },
        {
          orderBy: orderObject.orderBy,
          direction: orderObject.desc ? 'DESC' : 'ASC',
        }
      );
      setQuestionaires(reqQuestionaires.data);
      setPagination((old) => ({ ...old, total: reqQuestionaires.count }));
      setLoading(false);
      const questionaireIds = reqQuestionaires.data.map((item) => item.id);
      const { data: viewsData } =
        await Api.questionaire.getQuestionairesViewAmount({ questionaireIds });
      const mapped = reqQuestionaires.data.map((item) => ({
        ...item,
        views:
          viewsData.find((itemView) => itemView.itemId === item.id)?.views || 0,
      }));
      setQuestionaires(mapped);
    } catch (err) {
      toast.error(p('failedToFetchQuestionaires'));
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchQuestionaires();
  }, []);
  const deleteQuestionaire = async () => {
    setLoading(true);
    try {
      await Api.questionaire.deleteQuestionaire(deleteID);
      toast.success(p('questionaireDeleted'));
    } catch (err) {
      toast.error(p('questionaireDeleteError'));
    } finally {
      handleModal();
      fetchQuestionaires(pagination.currentPage, pagination.perPage, order);
    }
  };

  const onChangePage = (pager) => {
    if (
      pager.pageSize !== pagination.perPage ||
      pager.currentPage !== pagination.currentPage
    ) {
      setPagination((old) => ({
        ...old,
        currentPage: pager.currentPage,
        perPage: pager.pageSize,
      }));
      fetchQuestionaires(pager.currentPage, pager.pageSize, order);
    }
  };

  const renderSort = (cellOrder) => {
    if (order.orderBy !== cellOrder) {
      return undefined;
    }
    return order.desc ? <ArrowDownIcon size={16} /> : <ArrowUpIcon size={16} />;
  };
  const handleSort = (incommingOrder) => {
    const newOrder = {
      orderBy: incommingOrder,
      desc: incommingOrder === order.orderBy ? !order.desc : true,
    };
    setOrder(newOrder);
    setPagination({
      total: 0,
      currentPage: 1,
      perPage: 10,
    });
    fetchQuestionaires(1, 10, newOrder);
  };
  const getAvailableFor = (questionaire) => {
    const { access } = questionaire;
    if (!access) {
      return s('all');
    }
    if (access.levels || access.groups || access.tiers || access.segments) {
      return t('OfferForm.targetAudience');
    }
    return s('all');
  };
  const getBadgeStatus = (questionaire) => {
    if (!questionaire.visible) {
      return {
        color: 'danger',
        text: s('inActive'),
      };
    }
    if (moment().isAfter(moment(questionaire.endDate))) {
      return {
        color: 'secondary',
        text: s('closed'),
      };
    }
    if (moment().isBefore(moment(questionaire.startDate))) {
      return {
        color: 'warning',
        text: s('scheduled'),
      };
    }
    return {
      color: 'success',
      text: s('active'),
    };
  };

  // One quiz per picked match, holding that match's "guess the result"
  // question. Each opens now and closes when the match starts.
  const createMatchQuizzes = async (
    questions,
    { matches, categoryId, image, extraQuestions, subtitle, pushes }
  ) => {
    setShowMatchQuizzes(false);
    setLoading(true);
    let created = 0;
    let pushesSent = 0;
    let pushesFailed = 0;
    // The image is uploaded with the first quiz; the rest reuse its url
    let imageForQuiz = image || undefined;
    const libraryQuestions = (extraQuestions || []).map((item) => ({
      title: item.title,
      image: item.imageUrl,
      description: item.description,
      type: item.type,
      value: item.value,
      isRequired: false,
      headingVideos: item.headingVideos || [],
      imageHeaderType: item.imageHeaderType,
    }));
    // One at a time, so a failure doesn't stop the rest
    for (const [index, match] of matches.entries()) {
      const question = questions[index];
      try {
        const quiz = await Api.questionaire.createQuestionaire({
          type: 'QUIZ',
          title: question.title,
          subtitle,
          description: question.description,
          visible: true,
          editAnswers: true,
          // Match quizzes never show other people's answers
          answerVisibility: 'NOT_VISIBLE',
          leaderBoardVisibility: true,
          startDate: moment().toISOString(),
          endDate: moment.utc(match.matchStart).toISOString(),
          image: imageForQuiz,
        });
        if (quiz?.imageUrl) imageForQuiz = quiz.imageUrl;
        await Api.questionaire.linkQustions(quiz.id, {
          questions: [
            {
              title: question.title,
              description: question.description,
              type: question.type,
              value: question.value,
              isRequired: true,
              headingVideos: [],
              imageHeaderType: 'NONE',
            },
            ...libraryQuestions,
          ],
        });
        if (categoryId) {
          await Api.questionaire.categories.setQuestionaireCategory(quiz.id, {
            categoryId,
          });
        }
        created += 1;
        // A reminder push that opens this quiz, through its own
        // communication (scheduled, or right away if the time has passed)
        const push = pushes?.[index];
        if (push) {
          try {
            const scheduled = moment(push.sendAt).isAfter(
              moment().add(1, 'minute')
            );
            await Api.questionaire.contactQuestionaire(quiz.id, {
              title: push.title,
              message: push.body,
              type: 'push',
              tiers: [],
              levels: [],
              groups: [],
              notificationLabel: null,
              ...(scheduled && {
                scheduledDate: moment.utc(push.sendAt).format(),
              }),
            });
            pushesSent += 1;
          } catch {
            pushesFailed += 1;
          }
        }
      } catch {
        // counted below as not created
      }
    }
    const message = `${p('matchQuizzesCreated', {
      created,
      total: matches.length,
    })}${pushesSent ? ` · ${p('pushesScheduled', { count: pushesSent })}` : ''}`;
    if (created === matches.length && !pushesFailed) {
      toast.success(message);
    } else {
      toast.error(message);
    }
    setLoading(false);
    fetchQuestionaires(pagination.currentPage, pagination.perPage, order);
  };

  // A push to everyone that opens the app's home screen, at the poll's
  // opening time (right away if it already opens now), sent like the
  // general pushes on the messages page
  const sendPollPush = (push, opensAt) => {
    const sendAt = moment(opensAt);
    const scheduled = sendAt.isAfter(moment().add(1, 'minute'));
    const payload = {
      channels: [{ channel: 'push', templateId: 'push', pushLabel: 'general' }],
      dryRun: false,
      recipients: [{ userId: '' }],
      userGroups: ['all groups'],
      tiers: [],
      targetAudience: {
        type: 'all',
        tiers: [],
        groups: [],
        levels: [],
        segments: [],
      },
      groups: [],
      levels: [],
      segments: [],
      placeholders: {
        body: push.body,
        title: push.title,
        url: '/home/false',
      },
      senderInfo: { sender: `${config.id}`, type: 'Club' },
      scheduledDate: scheduled ? sendAt.utc().format() : null,
      type: 'GENERAL',
      notificationLabel: null,
    };
    return scheduled
      ? Api.scheduleMessages.sendMessage(payload)
      : Api.userDevices.sendMessage(payload);
  };

  // Polls before and/or after matches: each item is one poll with its one
  // question; the image is uploaded with the first and reused after that
  const createMatchPolls = async (_, { items, categoryId, image }) => {
    setShowMatchPolls(false);
    setLoading(true);
    let created = 0;
    let imageForPoll = image || undefined;
    let pushes = 0;
    let pushesFailed = 0;
    for (const item of items) {
      try {
        const poll = await Api.questionaire.createQuestionaire({
          type: 'POLL',
          title: item.title,
          subtitle: item.subtitle,
          description: item.description,
          visible: true,
          editAnswers: true,
          answerVisibility: 'SHOW_AFTER_ANSWERED',
          leaderBoardVisibility: false,
          startDate: item.startDate,
          endDate: item.endDate,
          image: imageForPoll,
        });
        if (poll?.imageUrl) imageForPoll = poll.imageUrl;
        await Api.questionaire.linkQustions(poll.id, {
          questions: item.questions,
        });
        if (categoryId) {
          await Api.questionaire.categories.setQuestionaireCategory(poll.id, {
            categoryId,
          });
        }
        created += 1;
        if (item.push) {
          try {
            await sendPollPush(item.push, item.startDate);
            pushes += 1;
          } catch {
            pushesFailed += 1;
          }
        }
      } catch {
        // counted below as not created
      }
    }
    const message = `${p('matchPollsCreated', {
      created,
      total: items.length,
    })}${pushes ? ` · ${p('pushesScheduled', { count: pushes })}` : ''}`;
    if (created === items.length && !pushesFailed) {
      toast.success(message);
    } else {
      toast.error(message);
    }
    setLoading(false);
    fetchQuestionaires(pagination.currentPage, pagination.perPage, order);
  };

  const renderQuestionaires = () => {
    return questionaires.map((questionaire, i) => {
      const badge = getBadgeStatus(questionaire);
      return (
        <tr
          style={{ background: i % 2 ? '#fff' : '#F9FAFB' }}
          key={`questionaire-${questionaire.id}`}
        >
          <td className="table-stack__primary">
            <div className="flex-Vcenter">
              <Link to={`/questionnaires/edit/${questionaire.id}`}>
                {questionaire.title}
              </Link>
            </div>
          </td>
          <td data-label={s('date')}>
            <div>
              {formatDateTime(questionaire.startDate)} -{' '}
              {questionaire.endDate === UNLIMITED_DATE
                ? ''
                : formatDateTime(questionaire.endDate)}
            </div>
            <small className="text-muted">
              {s('created')}: {formatDate(questionaire.createdAt)}
            </small>
          </td>
          <td data-label={t('EventsForm.availableFor')}>
            {getAvailableFor(questionaire)}
          </td>
          <td
            className="text-center"
            data-label={`${s('views')} / ${p('responses')}`}
          >
            {questionaire.views !== undefined ? (
              <div>{questionaire.views}</div>
            ) : (
              <Loading loading size={40} />
            )}
            <small className="text-muted">
              {questionaire.answersCount} {p('responses')}
            </small>
          </td>
          <td data-label={s('type')}>
            {p(`questionaireType-${questionaire.type}`)}
          </td>
          <td data-label={s('status')}>
            <Badge color={badge.color}>{badge.text}</Badge>
          </td>
          <td className="table-stack__actions">
            <div className="icon-actions">
              <SquareIconButton
                icon={<EditOutlineIcon />}
                onClick={() =>
                  history.push(`/questionnaires/edit/${questionaire.id}`)
                }
                title={s('edit')}
                id={`edit-${questionaire.id}`}
              />
              <SquareIconButton
                icon={<ContentCopyIcon />}
                onClick={() =>
                  history.push(`/questionnaires/clone/${questionaire.id}`)
                }
                title={s('clone')}
                id={`clone-${questionaire.id}`}
              />
              <SquareIconButton
                icon={<DeleteOutlineIcon />}
                onClick={() => handleModal(questionaire.id)}
                title={s('delete')}
                tone="danger"
                id={`delete-${questionaire.id}`}
              />
            </div>
          </td>
        </tr>
      );
    });
  };

  return (
    <>
      <DeleteModal
        visible={visible}
        type={p('theQuestionaire')}
        handleModal={handleModal}
        modalAction={deleteQuestionaire}
      />
      <MatchQuestionsModal
        mode="quizzes"
        isOpen={showMatchQuizzes}
        onClose={() => setShowMatchQuizzes(false)}
        onCreate={createMatchQuizzes}
        existingTitles={questionaires.map((q) => q.title)}
      />
      <MatchQuestionsModal
        mode="polls"
        isOpen={showMatchPolls}
        onClose={() => setShowMatchPolls(false)}
        onCreate={createMatchPolls}
      />
      <Row>
        <Col>
          <FormCard
            title={p('questionaires')}
            actions={
              <div className="d-flex align-items-center" style={{ gap: 8 }}>
                <UncontrolledDropdown>
                  <DropdownToggle
                    caret
                    size="sm"
                    color="secondary"
                    className="mb-0"
                  >
                    {p('createFromMatches')}
                  </DropdownToggle>
                  <DropdownMenu right className="dropdown-menu--modern">
                    <DropdownItem onClick={() => setShowMatchQuizzes(true)}>
                      {p('createMatchQuizzesButton')}
                    </DropdownItem>
                    <DropdownItem onClick={() => setShowMatchPolls(true)}>
                      {p('createMatchPollsButton')}
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
                <Link to="/questionnaires/create">
                  <Button size="sm" color="primary" className="mb-0">
                    {s('create')}
                  </Button>
                </Link>
              </div>
            }
            bodyClassName="form-card__body--flush"
            className="mb-3"
          >
            <Table responsive striped className="table--roomy table--stack">
              <thead>
                <tr>
                  <th
                    style={{ color: '#4D576A' }}
                    className="sortable"
                    onClick={() => handleSort('title')}
                  >
                    <div className="flex alignItems-center">
                      {s('title')} {renderSort('title')}
                    </div>
                  </th>
                  <th
                    style={{ color: '#4D576A' }}
                    className="sortable"
                    onClick={() => handleSort('createdAt')}
                  >
                    <div className="flex alignItems-center">
                      {s('date')} {renderSort('createdAt')}
                    </div>
                  </th>
                  <th style={{ color: '#4D576A' }}>
                    <div className="flex alignItems-center">
                      {t('EventsForm.availableFor')}
                    </div>
                  </th>
                  <th
                    style={{ color: '#4D576A' }}
                    className="sortable"
                    onClick={() => handleSort('answersCount')}
                  >
                    <div className="flex alignItems-center">
                      {s('views')} / {p('responses')}{' '}
                      {renderSort('answersCount')}
                    </div>
                  </th>
                  <th style={{ color: '#4D576A' }}>
                    <div className="flex alignItems-center">{s('type')}</div>
                  </th>
                  <th style={{ color: '#4D576A' }}>
                    <div className="flex alignItems-center">{s('status')}</div>
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {questionaires.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-muted">
                      {p('noQuestionaires')}
                    </td>
                  </tr>
                ) : (
                  renderQuestionaires()
                )}
              </tbody>
            </Table>
          </FormCard>
          <Pagination
            items={pagination.total}
            onChangePage={onChangePage}
            initialPage={pagination.currentPage}
          />
        </Col>
      </Row>
    </>
  );
};

export default QuestionaireListPage;
